import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Mic2, Plus, Pencil, Trash2, Search, Mail, Phone, Globe, Instagram, MapPin, Star, ArrowDownAZ, ArrowUpZA, LayoutGrid, List, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { translateError } from '@/crm/utils/translateError';
import ArtisteForm, { ArtisteRow } from '../components/ArtisteForm';
import { formatEUR } from '../utils';
import { useCrm } from '../store';
import { demoFilterValue, useDemoMode } from '../demoMode';
import { exportXlsx } from '../utils/exportXlsx';
import { useConfirm } from '../components/ConfirmDialog';

export default function Artistes() {
  const crm = useCrm();
  const confirm = useConfirm();
  const [rows, setRows] = useState<ArtisteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<Record<string, { ca: number; events: number }>>({});
  const [search, setSearch] = useState('');
  const [filterDomaine, setFilterDomaine] = useState('');
  const [sortBy, setSortBy] = useState<'nom_scene' | 'domaine' | 'ville' | 'confiance'>('nom_scene');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ArtisteRow | null>(null);

  const [demoMode] = useDemoMode();

  const load = async () => {
    setLoading(true);
    const demo = demoFilterValue();
    const [{ data, error }, paRes] = await Promise.all([
      supabase.from('artistes').select('*').is('deleted_at', null).order('nom_scene'),
      supabase.from('prestations_artistes').select('artiste_id, cout, prestation_id, is_demo, prestations!inner(prop_ttc, deleted_at, is_demo)').eq('inclus', true).eq('is_demo', demo),
    ]);
    if (error) toast.error(translateError(error.message));
    setRows((data || []) as ArtisteRow[]);
    const agg: Record<string, { ca: number; events: number }> = {};
    for (const r of (paRes.data || []) as any[]) {
      if (!r.artiste_id || r.prestations?.deleted_at) continue;
      if (r.prestations?.is_demo !== demo) continue;
      const k = r.artiste_id;
      if (!agg[k]) agg[k] = { ca: 0, events: 0 };
      agg[k].events += 1;
      agg[k].ca += Number(r.cout ?? r.prestations?.prop_ttc ?? 0);
    }
    setStats(agg);
    setLoading(false);
  };

  useEffect(() => { load(); }, [demoMode]);

  const domaines = useMemo(() => {
    // Fusion : référentiel CRM + domaines réellement utilisés par les artistes
    const s = new Set<string>(crm.domaines || []);
    rows.forEach((r) => {
      (r.domaines || []).forEach((d) => d && s.add(d));
      if (r.domaine_principal) s.add(r.domaine_principal);
    });
    return Array.from(s).sort();
  }, [rows, crm.domaines]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = rows.filter((r) => {
      if (filterDomaine) {
        const allDoms = [...(r.domaines || []), r.domaine_principal].filter(Boolean) as string[];
        if (!allDoms.includes(filterDomaine)) return false;
      }
      if (!q) return true;
      return [r.nom_scene, r.nom, r.prenom, r.email, r.tel, r.ville, r.domaine_principal, ...(r.domaines || []), ...(r.segments || []), ...(r.tags || [])]
        .filter(Boolean).join(' ').toLowerCase().includes(q);
    });
    const cmp = (a: ArtisteRow, b: ArtisteRow) => {
      let va = '', vb = '';
      if (sortBy === 'nom_scene') { va = (a.nom_scene || '').toLowerCase(); vb = (b.nom_scene || '').toLowerCase(); }
      else if (sortBy === 'domaine') { va = (a.domaine_principal || a.domaines?.[0] || '').toLowerCase(); vb = (b.domaine_principal || b.domaines?.[0] || '').toLowerCase(); }
      else if (sortBy === 'ville') { va = (a.ville || '').toLowerCase(); vb = (b.ville || '').toLowerCase(); }
      else if (sortBy === 'confiance') { return ((b.niveau_confiance ?? 0) - (a.niveau_confiance ?? 0)) * (sortDir === 'asc' ? -1 : 1); }
      return va.localeCompare(vb, 'fr') * (sortDir === 'asc' ? 1 : -1);
    };
    return [...list].sort(cmp);
  }, [rows, search, filterDomaine, sortBy, sortDir]);

  const handleSave = async (data: ArtisteRow) => {
    const payload: any = { ...data };
    delete payload.id;
    Object.keys(payload).forEach((k) => payload[k] === '' && (payload[k] = null));

    if (data.id) {
      const { error } = await supabase.from('artistes').update(payload).eq('id', data.id);
      if (error) { toast.error(translateError(error.message)); return; }
      toast.success('Artiste mis à jour');
    } else {
      const { error } = await supabase.from('artistes').insert(payload);
      if (error) { toast.error(translateError(error.message)); return; }
      toast.success('Artiste créé');
    }
    load();
  };

  const handleDelete = async (r: ArtisteRow) => {
    if (!r.id) return;
    const ok = await confirm({
      title: 'Mettre cet artiste à la corbeille ?',
      description: `« ${r.nom_scene} »`,
      confirmLabel: 'Mettre à la corbeille',
      destructive: true,
    });
    if (!ok) return;
    const { error } = await supabase.from('artistes').update({ deleted_at: new Date().toISOString() }).eq('id', r.id);
    if (error) toast.error(translateError(error.message));
    else { toast.success('Artiste mis à la corbeille'); load(); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-serif text-slate-100 flex items-center gap-2">
            <Mic2 className="text-amber-400" size={22} /> Artistes
          </h1>
          <p className="text-sm text-slate-400 mt-1">{filtered.length} fiche{filtered.length > 1 ? 's' : ''} — base complète multi-utilisateur.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              const stamp = new Date().toISOString().slice(0, 10);
              exportXlsx(`artistes-${stamp}.xlsx`, filtered, [
                { header: 'Nom de scène', key: 'nom_scene' as any, width: 26 },
                { header: 'Civilité', key: 'civilite' as any, width: 10 },
                { header: 'Nom', key: 'nom' as any, width: 18 },
                { header: 'Prénom', key: 'prenom' as any, width: 16 },
                { header: 'Statut', key: 'statut' as any, width: 12 },
                { header: 'Domaine principal', key: 'domaine_principal' as any, width: 20 },
                { header: 'Domaines', key: (r: any) => (r.domaines || []).join(', '), width: 28 },
                { header: 'Email', key: 'email' as any, width: 28 },
                { header: 'Téléphone', key: 'tel' as any, width: 16 },
                { header: 'Site web', key: 'site_web' as any, width: 24 },
                { header: 'Instagram', key: 'instagram' as any, width: 18 },
                { header: 'Adresse', key: 'adresse' as any, width: 30 },
                { header: 'CP', key: 'cp' as any, width: 8 },
                { header: 'Ville', key: 'ville' as any, width: 20 },
                { header: 'Département', key: 'departement' as any, width: 14 },
                { header: 'Pays', key: 'pays' as any, width: 12 },
                { header: 'Zone intervention', key: 'zone_intervention' as any, width: 22 },
                { header: 'Tarif base', key: 'tarif_base' as any, format: 'currency', width: 14 },
                { header: 'Frais déplacement', key: 'frais_deplacement' as any, format: 'currency', width: 14 },
                { header: 'TVA %', key: 'tva_pct' as any, format: 'number', width: 8 },
                { header: 'Statut juridique', key: 'statut_juridique' as any, width: 18 },
                { header: 'SIRET', key: 'siret' as any, width: 18 },
                { header: 'IBAN', key: 'iban' as any, width: 28 },
                { header: 'BIC', key: 'bic' as any, width: 14 },
                { header: 'Niveau confiance', key: 'niveau_confiance' as any, format: 'number', width: 10 },
                { header: 'Tags', key: (r: any) => (r.tags || []).join(', '), width: 22 },
                { header: 'Remarque interne', key: 'remarque_interne' as any, width: 40 },
              ], 'Artistes');
            }}
            title="Exporter en Excel"
            className="px-3 py-2.5 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/40 font-medium rounded-lg text-sm transition-colors flex items-center gap-2"
          >
            <FileSpreadsheet size={14} strokeWidth={1.5} /> Excel
          </button>
          <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-amber-500 hover:bg-amber-400 text-slate-950">
            <Plus size={16} className="mr-1" /> Nouvel artiste
          </Button>
        </div>
      </div>

      <Card className="p-3 bg-slate-900 border-slate-800">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-64">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Recherche nom, ville, domaine, tag…" className="pl-9 bg-slate-950 border-slate-800 text-slate-100 placeholder:text-slate-500" />
          </div>
          <select value={filterDomaine} onChange={(e) => setFilterDomaine(e.target.value)} className="bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm text-slate-100" title="Filtrer par domaine">
            <option value="">Tous les domaines</option>
            {domaines.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value as typeof sortBy)} className="bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm text-slate-100" title="Trier par">
            <option value="nom_scene">Nom de scène</option>
            <option value="domaine">Domaine</option>
            <option value="ville">Ville</option>
            <option value="confiance">Niveau de confiance</option>
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
            className="border-slate-800 bg-slate-950 text-slate-300 hover:bg-slate-800"
            title={sortDir === 'asc' ? 'Ordre croissant (A → Z)' : 'Ordre décroissant (Z → A)'}
          >
            {sortDir === 'asc' ? <ArrowDownAZ size={14} /> : <ArrowUpZA size={14} />}
          </Button>
          <div className="flex border border-slate-800 rounded-md overflow-hidden">
            <button onClick={() => setView('grid')} className={`px-2.5 py-2 text-sm ${view === 'grid' ? 'bg-amber-500/15 text-amber-400' : 'bg-slate-950 text-slate-400 hover:text-slate-200'}`} title="Vue cartes"><LayoutGrid size={14} /></button>
            <button onClick={() => setView('list')} className={`px-2.5 py-2 text-sm ${view === 'list' ? 'bg-amber-500/15 text-amber-400' : 'bg-slate-950 text-slate-400 hover:text-slate-200'}`} title="Vue liste"><List size={14} /></button>
          </div>
        </div>
      </Card>

      {loading ? (
        <p className="text-slate-400 text-sm">Chargement…</p>
      ) : filtered.length === 0 ? (
        <Card className="p-8 bg-slate-900 border-slate-800 text-center text-slate-400 text-sm">
          Aucun artiste. Cliquez sur « Nouvel artiste » pour commencer.
        </Card>
      ) : view === 'list' ? (
        <Card className="bg-slate-900 border-slate-800 overflow-hidden">
          <ul className="divide-y divide-slate-800">
            {filtered.map((r) => {
              const allDoms = Array.from(new Set([...(r.domaines || []), r.domaine_principal].filter(Boolean) as string[]));
              return (
                <li key={r.id} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800/40">
                  {r.photo_url ? (
                    <img src={r.photo_url} alt={r.nom_scene} className="w-10 h-10 rounded-md object-cover bg-slate-800 shrink-0" />
                  ) : (
                    <div className="w-10 h-10 rounded-md bg-slate-800 flex items-center justify-center text-amber-400 font-serif text-base shrink-0">
                      {r.nom_scene[0]?.toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-100 truncate font-medium">{r.nom_scene}</div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {[r.prenom, r.nom].filter(Boolean).join(' ')}
                      {(r.ville || r.departement) && <span> · {[r.ville, r.departement].filter(Boolean).join(' · ')}</span>}
                    </div>
                  </div>
                  <div className="hidden md:flex items-center gap-1 flex-wrap max-w-[40%] justify-end">
                    {allDoms.slice(0, 3).map((d) => (
                      <Badge key={d} variant="outline" className="border-violet-500/30 text-violet-400 bg-violet-500/10 text-[10px]">{d}</Badge>
                    ))}
                    {r.niveau_confiance != null && (
                      <span className="flex items-center gap-0.5 ml-1">
                        {Array.from({ length: r.niveau_confiance }).map((_, i) => <Star key={i} size={10} className="fill-amber-400 text-amber-400" />)}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => { setEditing(r); setOpen(true); }}>
                      <Pencil size={12} />
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-400 border-red-500/30 hover:bg-red-500/10" onClick={() => handleDelete(r)}>
                      <Trash2 size={12} />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((r) => (
            <Card key={r.id} className="p-4 bg-slate-900 border-slate-800 hover:border-amber-500/30 transition-colors flex flex-col">
              <div className="flex gap-3">
                {r.photo_url ? (
                  <img src={r.photo_url} alt={r.nom_scene} className="w-16 h-16 rounded-lg object-cover bg-slate-800" />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-slate-800 flex items-center justify-center text-amber-400 font-serif text-xl">
                    {r.nom_scene[0]?.toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-slate-100 text-base truncate">{r.nom_scene}</div>
                  {(r.prenom || r.nom) && <div className="text-xs text-slate-500 truncate">{[r.prenom, r.nom].filter(Boolean).join(' ')}</div>}
                  <div className="flex items-center gap-1.5 flex-wrap mt-1">
                    {(() => {
                      const allDoms = Array.from(new Set([...(r.domaines || []), r.domaine_principal].filter(Boolean) as string[]));
                      return allDoms.map((d) => (
                        <Badge key={d} variant="outline" className="border-violet-500/30 text-violet-400 bg-violet-500/10 text-[10px]">{d}</Badge>
                      ));
                    })()}
                    {r.statut && <Badge variant="outline" className="border-slate-700 text-slate-400 text-[10px] capitalize">{r.statut}</Badge>}
                    {r.niveau_confiance != null && (
                      <span className="flex items-center gap-0.5">
                        {Array.from({ length: r.niveau_confiance }).map((_, i) => <Star key={i} size={10} className="fill-amber-400 text-amber-400" />)}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-3 space-y-1 text-xs text-slate-400">
                {r.email && <div className="flex items-center gap-1.5 truncate"><Mail size={11} /> {r.email}</div>}
                {r.tel && <div className="flex items-center gap-1.5"><Phone size={11} /> {r.tel}</div>}
                {(r.ville || r.departement) && <div className="flex items-center gap-1.5"><MapPin size={11} /> {[r.ville, r.departement].filter(Boolean).join(' · ')}</div>}
                {r.site_web && <div className="flex items-center gap-1.5 truncate"><Globe size={11} /> {r.site_web}</div>}
                {r.instagram && <div className="flex items-center gap-1.5 truncate"><Instagram size={11} /> {r.instagram}</div>}
              </div>

              {(r.tarif_base != null || r.frais_deplacement != null || (r.id && stats[r.id])) && (
                <div className="mt-3 pt-3 border-t border-slate-800 flex flex-wrap gap-3 text-xs">
                  {r.tarif_base != null && <div><span className="text-slate-500">Base HT</span> <span className="text-amber-400 font-medium">{formatEUR(Number(r.tarif_base))}</span></div>}
                  {r.frais_deplacement != null && <div><span className="text-slate-500">Frais</span> <span className="text-slate-200">{formatEUR(Number(r.frais_deplacement))}</span></div>}
                  {r.id && stats[r.id] && (
                    <>
                      <div><span className="text-slate-500">CA cumulé</span> <span className="text-emerald-400 font-medium">{formatEUR(stats[r.id].ca)}</span></div>
                      <div><span className="text-slate-500">Évén.</span> <span className="text-sky-300">{stats[r.id].events}</span></div>
                    </>
                  )}
                </div>
              )}

              {r.tags && r.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {r.tags.map((t) => <Badge key={t} variant="outline" className="text-[10px] border-amber-500/20 text-amber-400/80">{t}</Badge>)}
                </div>
              )}

              <div className="flex gap-2 mt-4 pt-3 border-t border-slate-800">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => { setEditing(r); setOpen(true); }}>
                  <Pencil size={12} className="mr-1" /> Modifier
                </Button>
                <Button size="sm" variant="outline" className="text-red-400 border-red-500/30 hover:bg-red-500/10" onClick={() => handleDelete(r)}>
                  <Trash2 size={12} />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <ArtisteForm open={open} initial={editing} onClose={() => setOpen(false)} onSave={handleSave} />
    </div>
  );
}
